import IonAlert from '../../../../../components/Modal/ModalAlert';
import IonToolbar from '../../../../../components/Modal/ModalToolbar';
import { IonButton, IonButtons, IonContent, IonFooter, IonHeader, IonIcon, IonInput, IonLabel, IonLoading, IonPage, IonRadio, IonRadioGroup, IonTitle, IonToast, IonToggle, useIonViewDidEnter } from '@ionic/react';
import { Buffer } from 'buffer';
import { arrowBack } from 'ionicons/icons';
import React, { useContext, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { empresaAlterar, empresaConfiguracoesCarregar, requestService } from '../../../../../Utils/Services';
import { MessageContext } from '../../../../../contexts/MessageContext';
import './Cashback.css';

type Props = {
  abrirMenu: boolean;
};

// Constantes
const MIN_PERCENTUAL = 0;
const MAX_PERCENTUAL = 20;
const MAX_DIGITOS_INTEIROS = 2;

const Cashback: React.FC<Props> = (props: Props) => {
  const history = useHistory();
  const { loja, setEsconderMenu, setNomeMenu } = useContext(MessageContext);
  const [acessos] = useState<any>(JSON.parse(window.localStorage.getItem("acessos") || '{}'));

  // Estados de UI
  const [showToast, setShowToast] = useState(false);
  const [textToast, setTextToast] = useState("");
  const [showLoading, setShowLoading] = useState(true);
  const [showSucesso, setShowSucesso] = useState(false);
  const [showConfirmAlert, setShowConfirmAlert] = useState(false);

  // Estados de configurações
  const [cashbackAtivado, setCashbackAtivado] = useState(false);
  const [percentualCashback, setPercentualCashback] = useState('');
  const [prazoCashback, setPrazoCashback] = useState(15);

  useIonViewDidEnter(() => {
    setEsconderMenu(true);
    setNomeMenu("");
    carregarConfiguracoes();
  });

  // Funções de formatação e validação
  const formatarPercentual = (valor: string): string => {
    if (!valor || valor === '') return '';

    // Remove TUDO exceto números - apenas inteiros
    let limpo = valor.replace(/\D/g, '');

    return limpo;
  };

  const validarPercentual = (valor: string): boolean => {
    if (!valor || valor === '') return false;

    const numero = parseInt(valor, 10);
    return !isNaN(numero) && numero > MIN_PERCENTUAL && numero <= MAX_PERCENTUAL;
  };

  const handlePercentualChange = (e: any) => {
    let inputValue = e.target.value;

    // Se vazio, limpa
    if (inputValue === '') {
      setPercentualCashback('');
      return;
    }

    // Formata o valor (apenas números inteiros)
    inputValue = formatarPercentual(inputValue);

    // Se ficou vazio após formatação, limpa
    if (inputValue === '') {
      setPercentualCashback('');
      return;
    }

    // Limita a MAX_DIGITOS_INTEIROS
    if (inputValue.length > MAX_DIGITOS_INTEIROS) {
      // Pega apenas os primeiros MAX_DIGITOS_INTEIROS dígitos
      inputValue = inputValue.substring(0, MAX_DIGITOS_INTEIROS);
    }

    // Valida se está dentro do limite e ajusta automaticamente
    const numero = parseInt(inputValue, 10);
    if (!isNaN(numero) && numero > MAX_PERCENTUAL) {
      // Se ultrapassou, ajusta para o máximo permitido
      setPercentualCashback(MAX_PERCENTUAL.toString());
      return;
    }

    setPercentualCashback(inputValue);
  };

  const handlePercentualBlur = () => {
    // Quando sai do campo, simplesmente valida se está vazio e limpa se necessário
    if (percentualCashback && percentualCashback !== '') {
      const numero = parseInt(percentualCashback, 10);
      if (isNaN(numero) || numero <= 0) {
        setPercentualCashback('');
      }
    }
  };

  // Funções de API
  const carregarConfiguracoes = async () => {
    try {
      const id = loja;
      const url = empresaConfiguracoesCarregar.url
        .replace("{id}", id.replaceAll(/#/g, '%23'))
        .concat("?filtro=", Buffer.from(loja).toString("base64"));

      requestService(
        url,
        {
          method: empresaConfiguracoesCarregar.method,
          headers: [["token", window.localStorage.getItem("token")]],
          credentials: 'include'
        },
        history,
        (response: any) => {
          if (response.Item) {
            setCashbackAtivado(response.Item.cashbackAtivado || false);
            const percentual = response.Item.percentualCashback || 5;
            // Arredonda para inteiro ao carregar
            setPercentualCashback(Math.round(parseFloat(percentual.toString())).toString());
            setPrazoCashback(response.Item.prazoCashback || 15);
          }
          setShowLoading(false);
        },
        (error: any) => {
          console.error('Erro ao carregar configurações:', error);
          setShowLoading(false);
          exibirToast("Erro ao carregar configurações.");
        }
      );
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
      setShowLoading(false);
      exibirToast("Erro ao carregar configurações.");
    }
  };

  const salvarConfiguracoes = async () => {
    setShowLoading(true);

    try {
      // Converte para número inteiro ao salvar
      const percentualNumerico = parseInt(percentualCashback, 10);

      const objectRequest = {
        cashbackAtivado,
        percentualCashback: percentualNumerico.toString(),
        prazoCashback
      };

      const id = loja;
      const url = empresaAlterar.url
        .replace("{id}", id.replaceAll("#", "%23"))
        .concat("?tipo=cashback");

      await requestService(
        url,
        {
          method: empresaAlterar.method,
          body: JSON.stringify(objectRequest),
          headers: [["token", window.localStorage.getItem("token")]],
          credentials: 'include'
        },
        history,
        (response: any) => {
          if (response) {
            setTextToast("Configurações de cashback salvas com sucesso!");
            setShowSucesso(true);
          } else {
            exibirToast("Ocorreu um erro ao salvar as configurações.");
          }
          setShowLoading(false);
        },
        (error: any) => {
          console.error('Erro ao salvar configurações:', error);
          exibirToast("Ocorreu um erro ao salvar as configurações.");
          setShowLoading(false);
        }
      );
    } catch (error) {
      console.error('Erro ao salvar configurações:', error);
      exibirToast("Ocorreu um erro ao salvar as configurações.");
      setShowLoading(false);
    }
  };

  // Funções auxiliares
  const validarESalvar = () => {
    if (!validarPercentual(percentualCashback)) {
      exibirToast(`Por favor, insira um percentual válido entre ${MIN_PERCENTUAL} e ${MAX_PERCENTUAL}.`);
      return;
    }

    setShowConfirmAlert(true);
  };

  const exibirToast = (mensagem: string) => {
    setTextToast(mensagem);
    setShowToast(true);
  };

  const podeEditar = acessos?.configuracoes === 'edicao';

  return (
    <IonPage className="cashback-page">
      <IonHeader className="ion-no-border">
        <IonToolbar className="cashback-toolbar">
          <IonButtons slot="start">
            <IonButton onClick={() => history.goBack()}>
              <IonIcon slot="icon-only" icon={arrowBack} />
            </IonButton>
          </IonButtons>
          <IonTitle>Cashback</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent className="cashback-content">
        <div className="cashback-container">
        {/* Descrição */}
        <div className="cashback-intro">
          <h1>Configurações de cashback</h1>
          <p className="cashback-description">
            Aumente suas vendas e fidelize seus clientes oferecendo créditos para que façam novos pedidos.
            Configure o Cashback de acordo com a melhor estratégia para seu estabelecimento.
          </p>
        </div>

        <div className="cashback-sections">
        {/* Card Cashback */}
        <div className="cashback-card">
          {/* Header do card com toggle */}
          <div className="cashback-card-heading">
            <div className="cashback-heading-copy">
              <h2 className="cashback-card-title">
                Cashback
              </h2>
              <p className="cashback-description">
                O jeito mais fácil de fidelizar seus clientes com um percentual em cima do valor do pedido
              </p>
            </div>
            <div className="cashback-toggle">
              <IonToggle
                aria-label="Ativar cashback"
                checked={cashbackAtivado}
                onIonChange={(e) => setCashbackAtivado(e.detail.checked)}
                disabled={!podeEditar}
              />
            </div>
          </div>

          {/* Campo Percentual */}
          <div className="cashback-card-body">
            <IonLabel position="stacked">
              <strong>Percentual*</strong>
            </IonLabel>
            <div className="cashback-input-wrap">
              <IonInput
                aria-label="Percentual de cashback"
                type="text"
                value={percentualCashback}
                placeholder="5"
                maxlength={2}
                disabled={!cashbackAtivado || !podeEditar}
                onIonInput={handlePercentualChange}
                onIonBlur={handlePercentualBlur}
                className="cashback-input"
              />
              <span className="cashback-suffix">
                %
              </span>
            </div>
            <p className="cashback-help">
              Valor entre {MIN_PERCENTUAL + 1} e {MAX_PERCENTUAL}%
            </p>

            {/* Campo Prazo de expiração */}
            <div className="cashback-expiration">
              <IonLabel position="stacked">
                <strong>Prazo de expiração</strong>
              </IonLabel>
              <IonRadioGroup
                value={prazoCashback}
                onIonChange={(e) => setPrazoCashback(e.detail.value)}
                className="cashback-options"
              >
                {[15, 30, 45, 60].map((dias) => (
                  <label
                    key={dias}
                    className={`cashback-option${prazoCashback === dias ? ' cashback-option--selected' : ''}${!cashbackAtivado || !podeEditar ? ' cashback-option--disabled' : ''}`}
                  >
                    <IonRadio
                      value={dias}
                      disabled={!cashbackAtivado || !podeEditar}
                      color="primary"
                      className="cashback-radio"
                    />
                    <span className="cashback-option-text">
                      {dias} dias
                    </span>
                  </label>
                ))}
              </IonRadioGroup>
            </div>
          </div>
        </div>

        {/* Card Regras de uso */}
        <div className="cashback-card cashback-rules">
          <div className="cashback-card-body">
            <h3 className="cashback-rules-title">
              Regras de uso
            </h3>

            <ul className="cashback-rules-list">
              <li>
                <strong>Como resgatar créditos acumulados?</strong>
                <br />
                Ao finalizar o pedido, seu cliente seleciona a opção "Adicionar Cashback" e o saldo será descontado do valor total do pedido. Só é possível resgatar o valor total do saldo disponível.
              </li>
              <li className="cashback-rule">
                <strong>Como é calculado o valor do cashback?</strong>
                <br />
                O cashback é concedido para pedidos realizados e é calculado em cima do valor do pedido.
              </li>
              <li className="cashback-rule">
                <strong>O que acontece se eu desativar o Cashback?</strong>
                <br />
                Se você desativar o Cashback, o cliente não pode mais resgatar o saldo acumulado.
              </li>
            </ul>
          </div>
        </div>

        </div>
        </div>
        {/* Toasts e Alerts */}
        <IonToast
          isOpen={showToast}
          position="middle"
          onDidDismiss={() => setShowToast(false)}
          message={textToast}
          duration={3000}
        />

        <IonAlert
          isOpen={showConfirmAlert}
          onDidDismiss={() => setShowConfirmAlert(false)}
          header={'Salvar configurações'}
          message={'Você <strong>confirma</strong> as alterações nas configurações de cashback?'}
          buttons={[
            {
              text: 'Cancelar',
              role: 'cancel',
            },
            {
              text: 'Confirmar',
              cssClass: 'primary',
              handler: () => {
                salvarConfiguracoes();
              }
            }
          ]}
        />

        <IonLoading
          cssClass='my-custom-class'
          isOpen={showLoading}
          onDidDismiss={() => setShowLoading(false)}
          spinner='circles'
        />

        <IonLoading
          cssClass='my-custom-class'
          isOpen={showSucesso}
          onDidDismiss={() => { setShowSucesso(false); history.goBack(); }}
          message={'Configurações salvas com sucesso!'}
          duration={3000}
          animated={false}
        />
      </IonContent>

      <IonFooter className="ion-no-border cashback-footer">
        <IonToolbar>
          <IonButton
            onClick={validarESalvar}
            expand="block"
            type="submit"
            disabled={!podeEditar}
          >
            Salvar
          </IonButton>
        </IonToolbar>
      </IonFooter>
    </IonPage>
  );
};

export default Cashback;